import type { Kpi, KpiScorecardPayload } from '@diagon-alley/common';
import { Card, Group, Stack, Text } from '@mantine/core';
import { ArrowDown, ArrowUp, Minus } from '@phosphor-icons/react';
import { formatMoney, formatCount } from '@utils/currency';

import styles from './ReportsContainer.module.css';

interface KpiScorecardBlockProps {
    payload: KpiScorecardPayload;
}

export default function KpiScorecardBlock({ payload }: KpiScorecardBlockProps) {
    return (
        <div className={styles.scorecardWrap}>
            {payload.title && (
                <Text size="sm" fw={500} c="dimmed" mb="xs">
                    {payload.title}
                </Text>
            )}
            <div className={styles.scorecardGrid}>
                {payload.kpis.map((kpi, i) => (
                    <KpiCard key={`${kpi.label}-${i}`} kpi={kpi} />
                ))}
            </div>
        </div>
    );
}

function KpiCard({ kpi }: { kpi: Kpi }) {
    return (
        <Card withBorder radius="md" p="md" className={styles.kpiCard}>
            <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                    {kpi.label}
                </Text>
                <Group gap={6} align="baseline" wrap="nowrap">
                    <Text size="xl" fw={600}>
                        {formatValue(kpi.value, kpi.unit)}
                    </Text>
                    {kpi.unit && kpi.unit !== '₹' && kpi.unit !== '%' && (
                        <Text size="xs" c="dimmed">
                            {kpi.unit}
                        </Text>
                    )}
                </Group>
                <DeltaRow kpi={kpi} />
            </Stack>
        </Card>
    );
}

function DeltaRow({ kpi }: { kpi: Kpi }) {
    if (kpi.deltaPct == null) {
        return (
            <Text size="xs" c="dimmed">
                {kpi.deltaLabel ?? ' '}
            </Text>
        );
    }

    const direction = kpi.direction ?? 'flat';
    const color =
        direction === 'up'
            ? 'teal.7'
            : direction === 'down'
              ? 'red.7'
              : 'gray.6';
    const Icon =
        direction === 'up'
            ? ArrowUp
            : direction === 'down'
              ? ArrowDown
              : Minus;

    return (
        <Group gap={4} align="center" wrap="nowrap">
            <Icon size={12} weight="bold" />
            <Text size="xs" c={color} fw={500}>
                {kpi.deltaPct >= 0 ? '+' : ''}
                {kpi.deltaPct.toFixed(1)}%
            </Text>
            {kpi.deltaLabel && (
                <Text size="xs" c="dimmed">
                    {kpi.deltaLabel}
                </Text>
            )}
        </Group>
    );
}

function formatValue(value: number, unit?: string): string {
    if (unit === '₹') return formatMoney(value);
    if (unit === '%') return `${value.toFixed(1)}%`;
    return formatCount(value);
}
